import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, MessageCircle, MapPin, Phone, ExternalLink, ChevronDown } from "lucide-react";
import { imgSrc } from "@/lib/img";
import { Switch } from "@/components/ui-kit";
import { TypeBadge } from "@/components/VenueBits";
import { ReasonDialog } from "@/components/BookingStatus";
import { api, errorText } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { usePrefs } from "@/contexts/PrefsContext";
import type { Venue } from "@/types";
import { useLive } from "@/lib/live";

const waNum = (p: string) => {
  let d = p.replace(/\D/g, "");
  if (d.startsWith("00")) d = d.slice(2);
  if (d.startsWith("07")) d = "964" + d.slice(1);
  return d;
};

const ReviewCard = ({ v, canDecide, onApprove, onReject }: { v: Venue; canDecide: boolean; onApprove: (events: boolean) => void; onReject: () => void }) => {
  const { t, lang } = usePrefs();
  const [open, setOpen] = useState(false);
  const [events, setEvents] = useState(false);
  const acc = v.account || {};
  const msg = t("vr.waMsg").replace("{business}", acc.business_name || "").replace("{place}", v.name_ar);
  const pill = "h-9 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 active:scale-95 transition-transform";
  return (
    <div data-testid={`venue-review-${v.venue_id}`} className="rounded-2xl border border-amber-500/40 bg-card min-w-0">
      <button onClick={() => setOpen(!open)} aria-expanded={open} data-testid={`venue-review-toggle-${v.venue_id}`} className="w-full p-2.5 flex items-center gap-3 text-start">
        {v.image ? <img src={imgSrc(v.image)} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : <span className="h-14 w-14 shrink-0 rounded-xl bg-muted" />}
        <span className="flex-1 min-w-0">
          <span className="flex items-center gap-1.5 min-w-0">
            <b className="truncate">{v.name_ar}</b>
            {v.is_edit && <span data-testid={`venue-review-edit-badge-${v.venue_id}`} className="shrink-0 rounded-full bg-violet-500/15 text-violet-600 dark:text-violet-400 px-2 py-0.5 text-[10px] font-bold">{t("pp.editBadge")}</span>}
            {v.resubmitted_at && <span className="shrink-0 rounded-full bg-sky-500/15 text-sky-600 dark:text-sky-400 px-2 py-0.5 text-[10px] font-bold">{t("vr.resubmitted")}</span>}
          </span>
          <span className="block text-xs text-muted-foreground truncate">{[acc.business_name, v.city].filter(Boolean).join(" · ")}</span>
          <span className="block text-[11px] text-muted-foreground">{fmtDateTime(v.resubmitted_at || v.created_at || "", lang)}</span>
          {typeof v.lat !== "number" && <span data-testid={`venue-review-no-location-${v.venue_id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-500/15 text-amber-700 dark:text-amber-400 px-2 py-0.5 text-[10px] font-bold"><MapPin className="h-3 w-3" />{t("pp.noLocationAdmin")}</span>}
        </span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div data-testid={`venue-review-details-${v.venue_id}`} className="px-3 pb-2 text-xs text-muted-foreground space-y-1">
          <TypeBadge type={v.type} />
          {v.name_en && <p dir="ltr">{v.name_en}</p>}
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {v.address && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{v.address}</span>}
            {acc.phone && <span className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" /><bdi dir="ltr">{acc.phone}</bdi></span>}
          </div>
          {v.maps_url && <a href={v.maps_url} target="_blank" rel="noreferrer" className="flex items-center gap-1 text-primary font-bold"><ExternalLink className="h-3.5 w-3.5" />{t("admin.maps")}</a>}
          {v.description_ar && <p className="text-foreground line-clamp-4">{v.description_ar}</p>}
          {v.is_edit && v.pending_changes && (
            <ul data-testid={`venue-review-changes-${v.venue_id}`} className="rounded-xl bg-accent p-2 space-y-0.5 text-foreground">
              <li className="font-bold">{t("pp.changes")}</li>
              {Object.entries(v.pending_changes).map(([k, val]) => <li key={k} className="truncate"><b>{t(`pp.f.${k}`)}:</b> {Array.isArray(val) ? t("pp.photosN").replace("{n}", String(val.length)) : String(val ?? "")}</li>)}
            </ul>
          )}
        </div>
      )}
      <div className="px-2.5 pb-2.5 flex flex-wrap gap-1.5">
        {acc.phone && (
          <a href={`https://wa.me/${waNum(acc.phone)}?text=${encodeURIComponent(msg)}`} target="_blank" rel="noreferrer" data-testid={`venue-review-whatsapp-${v.venue_id}`} className={`${pill} bg-[#25D366] text-white hover:bg-[#1ebe5b]`}>
            <MessageCircle className="h-4 w-4" />{t("pc.wa")}
          </a>
        )}
        {canDecide && !v.is_edit && (
          <label data-testid={`venue-review-events-${v.venue_id}`} className="w-full flex items-center justify-between gap-2 rounded-xl border px-3 py-2 text-xs font-bold">
            {t("pp.eventsQuestion")}<Switch data-testid={`venue-review-events-switch-${v.venue_id}`} checked={events} onCheckedChange={setEvents} />
          </label>
        )}
        {canDecide && <>
          <button onClick={() => onApprove(events)} data-testid={`venue-review-approve-${v.venue_id}`} className={`${pill} bg-emerald-600 text-white`}><Check className="h-4 w-4" />{t("vr.approve")}</button>
          <button onClick={onReject} data-testid={`venue-review-reject-${v.venue_id}`} className={`${pill} bg-primary/10 text-primary`}><X className="h-4 w-4" />{t("vr.reject")}</button>
        </>}
      </div>
    </div>
  );
};

export const VenueReviewList = ({ canDecide, onDecided }: { canDecide: boolean; onDecided?: () => void }) => {
  const { t } = usePrefs();
  const [list, setList] = useState<Venue[]>([]);
  const [rejecting, setRejecting] = useState<Venue | null>(null);
  const [presets, setPresets] = useState<string[]>([]);
  const load = () => api.get("/admin/venue-reviews").then((r) => setList(r.data)).catch(() => {});
  useLive(() => load());
  useEffect(() => { load(); }, []);
  useEffect(() => { if (canDecide) api.get("/admin/reject-reasons").then((r) => setPresets(r.data)).catch(() => {}); }, [canDecide]);
  const decide = async (v: Venue, approve: boolean, reason = "", events_enabled = false) => {
    try {
      await api.post(`/admin/venues/${v.venue_id}/review`, { approve, reason, events_enabled });
      toast.success(t(approve ? "vr.approved" : "vr.rejected"));
      setRejecting(null);
      onDecided?.();
    } catch (e) {
      toast.error(errorText(e, t));
    }
    load();
  };
  if (!list.length) return null;
  return (
    <section data-testid="venue-reviews-section" className="mb-8 space-y-3">
      <h2 className="font-display text-xl font-black">{t("vr.title")}<span className="ms-2 inline-flex align-middle rounded-full bg-primary/10 text-primary px-2.5 py-0.5 text-sm font-bold" dir="ltr">{list.length}</span></h2>
      <div className="grid gap-2 lg:grid-cols-2 items-start">
        {list.map((v) => <ReviewCard key={v.venue_id} v={v} canDecide={canDecide} onApprove={(ev) => decide(v, true, "", ev)} onReject={() => setRejecting(v)} />)}
      </div>
      {rejecting && <ReasonDialog testid="venue-review-reject-dialog" title={t("vr.reject")} required presets={presets} onClose={() => setRejecting(null)} onSubmit={(r) => decide(rejecting, false, r)} />}
    </section>
  );
};
